import React, { useEffect, useState, useCallback } from 'react';
import { Building2, Plus, Trash2, Save } from 'lucide-react';
import { faceAPI } from '../api/api';

const ETIQUETA = {
  oficina_contable: 'Oficina Contable',
  organo_gestor: 'Órgano Gestor',
  unidad_tramitadora: 'Unidad Tramitadora',
  organo_proponente: 'Órgano Proponente',
};

// Gestión de los destinos FACe (códigos DIR3) de un cliente.
// Un mismo cliente puede tener varios destinos (una por concejalía/unidad).
export default function FaceDestinos({ clienteId }) {
  const [esAdmin, setEsAdmin] = useState(false);
  const [destinos, setDestinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await faceAPI.getDestinos(clienteId);
      setEsAdmin(!!data.es_administracion_publica);
      setDestinos(data.face_destinos || []);
    } catch (e) {
      // si el cliente no existe o falla, dejamos vacío
    }
    setLoading(false);
  }, [clienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  const setField = (i, campo, val) =>
    setDestinos((ds) => ds.map((d, idx) => (idx === i ? { ...d, [campo]: val } : d)));
  const addDestino = () =>
    setDestinos((ds) => [...ds, { nombre: '', nombre_interno: '', oficina_contable: '', organo_gestor: '', unidad_tramitadora: '', organo_proponente: '' }]);
  const removeDestino = (i) => setDestinos((ds) => ds.filter((_, idx) => idx !== i));

  const guardar = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await faceAPI.updateDestinos(clienteId, { es_administracion_publica: esAdmin, destinos });
      setDestinos(data.face_destinos || []);
      setMsg({ ok: true, txt: 'Guardado correctamente' });
    } catch (e) {
      setMsg({ ok: false, txt: 'Error al guardar' });
    }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <div className="mb-6" data-testid="face-destinos">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-gray-600" /> Facturación FACe (administración pública)
        </h3>
        <div className="flex items-center gap-3">
          {msg && <span className={`text-xs ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>{msg.txt}</span>}
          <button
            onClick={guardar}
            disabled={saving}
            className="text-xs px-3 py-1.5 bg-duetto-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
          >
            <Save className="w-3 h-3" /> {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-3">
        <input type="checkbox" checked={esAdmin} onChange={(e) => setEsAdmin(e.target.checked)} />
        Este cliente es administración pública (se factura por FACe)
      </label>

      {destinos.length === 0 && (
        <p className="text-sm text-gray-400 mb-2">
          Sin destinos DIR3. Añade uno manualmente o impórtalos desde Ajustes → Facturación.
        </p>
      )}

      <div className="space-y-3">
        {destinos.map((d, i) => (
          <div key={d.id || i} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-start justify-between mb-2 gap-2">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="flex justify-between text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                    <span>Nombre del organismo (centros DIR3)</span>
                    <span className={(d.nombre || '').length >= 40 ? 'text-amber-600 font-semibold' : ''}>
                      {(d.nombre || '').length}/40
                    </span>
                  </label>
                  <input
                    value={d.nombre || ''}
                    onChange={(e) => setField(i, 'nombre', e.target.value)}
                    maxLength={40}
                    placeholder="Concejalía / unidad (ej: EDUCACIÓN)"
                    title="Nombre de la unidad/organismo que acompaña a los códigos DIR3. No es la razón social del cliente (esa va en Nombre Fiscal). Máximo 40 caracteres (campo Name de Facturae)."
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-medium"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Nombre interno (solo ERP, no se envía)</label>
                  <input
                    value={d.nombre_interno || ''}
                    onChange={(e) => setField(i, 'nombre_interno', e.target.value)}
                    placeholder="(por defecto, el nombre oficial)"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
              </div>
              <button onClick={() => removeDestino(i)} className="text-gray-400 hover:text-red-500 mt-4" title="Eliminar destino">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {['oficina_contable', 'organo_gestor', 'unidad_tramitadora', 'organo_proponente'].map((campo) => (
                <div key={campo}>
                  <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{ETIQUETA[campo]}</label>
                  <input
                    value={d[campo] || ''}
                    onChange={(e) => setField(i, campo, e.target.value)}
                    placeholder={campo === 'organo_proponente' ? 'opcional' : ''}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button onClick={addDestino} className="mt-3 text-sm text-duetto-primary flex items-center gap-1 hover:underline">
        <Plus className="w-4 h-4" /> Añadir destino
      </button>
    </div>
  );
}
